import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { PageLayout } from '../../../components/organisms/PageLayout';
import { api, setImpersonationToken, setImpersonationExpiresAt } from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';
import '../../../styles/admin.css';

interface AdminUser {
  uid: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  is_admin: boolean;
  is_banned: boolean;
  created_at: string;
  last_login_at: string | null;
  plan_name: string;
  plan_code: string;
  cv_count: number;
  job_count: number;
  contact_count: number;
}

interface UsersResponse {
  users: AdminUser[];
  total: number;
  page: number;
  page_size: number;
}

const PLAN_BADGE: Record<string, string> = {
  FREE_PLAN: 'admin-badge--gray',
  TRIAL_PLAN: 'admin-badge--blue',
  PRO_PLAN: 'admin-badge--green',
};

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<UsersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchUsers = useCallback(async (q: string, p: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), page_size: '50' });
      if (q) params.set('search', q);
      const result = await api.get<UsersResponse>(`${API_ENDPOINTS.admin.users}?${params}`);
      setData(result);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Admin | Users';
    fetchUsers(search, page);
  }, [fetchUsers, search, page]);

  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleToggleBan = async (user: AdminUser) => {
    setActionLoading(user.uid);
    try {
      await api.patch(API_ENDPOINTS.admin.updateUser(user.uid), { is_banned: !user.is_banned });
      fetchUsers(search, page);
    } catch {
    } finally {
      setActionLoading(null);
    }
  };

  const handleImpersonate = async (user: AdminUser) => {
    setActionLoading(`imp-${user.uid}`);
    try {
      const res = await api.post<{ access_token: string; target_email: string; expires_at: number }>(
        API_ENDPOINTS.admin.impersonate(user.uid), {}
      );
      setImpersonationToken(res.access_token);
      if (res.expires_at) setImpersonationExpiresAt(res.expires_at);
      window.location.href = '/welcome';
    } catch {
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = data ? Math.ceil(data.total / data.page_size) : 1;

  return (
    <PageLayout breadcrumbItems={[{ label: 'Admin' }, { label: 'Users' }]}>
      <nav className="admin-nav">
        <NavLink to="/admin" end className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Overview</NavLink>
        <NavLink to="/admin/users" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Users</NavLink>
        <NavLink to="/admin/ai-usage" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>AI Usage</NavLink>
        <NavLink to="/admin/ai-settings" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>AI Settings</NavLink>
        <NavLink to="/admin/plans" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Plans</NavLink>
        <NavLink to="/admin/blog" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Blog</NavLink>
      </nav>

      <div className="admin-toolbar">
        <input
          className="admin-search"
          placeholder="Search by email or name…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
        />
        {data && <span style={{ fontSize: 13, color: '#6b7280' }}>{data.total} users</span>}
      </div>

      {loading && <div className="admin-loading">Loading…</div>}

      {!loading && data && (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Plan</th>
                  <th>CVs</th>
                  <th>Jobs</th>
                  <th>Contacts</th>
                  <th>Last login</th>
                  <th>Flags</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.users.length === 0 && (
                  <tr><td colSpan={9} className="admin-empty">No users found</td></tr>
                )}
                {data.users.map(user => (
                  <tr key={user.uid}>
                    <td>
                      <a
                        href={`/admin/users/${user.uid}`}
                        style={{ color: '#D85A3A', textDecoration: 'none', fontWeight: 500 }}
                      >
                        {user.email}
                      </a>
                    </td>
                    <td>{[user.first_name, user.last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td>
                      <span className={`admin-badge ${PLAN_BADGE[user.plan_code] || 'admin-badge--gray'}`}>
                        {user.plan_name}
                      </span>
                    </td>
                    <td>{user.cv_count}</td>
                    <td>{user.job_count}</td>
                    <td>{user.contact_count}</td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 13, color: user.last_login_at ? '#374151' : '#9ca3af' }}>
                      {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {user.is_admin && <span className="admin-badge admin-badge--orange">Admin</span>}
                        {user.is_banned && <span className="admin-badge admin-badge--red">Banned</span>}
                      </div>
                    </td>
                    <td>
                      <div className="admin-btn-group">
                        <button
                          className={`admin-btn ${user.is_banned ? 'admin-btn--success' : 'admin-btn--danger'}`}
                          onClick={() => handleToggleBan(user)}
                          disabled={actionLoading === user.uid}
                        >
                          {user.is_banned ? 'Unban' : 'Ban'}
                        </button>
                        <button
                          className="admin-btn admin-btn--ghost"
                          onClick={() => handleImpersonate(user)}
                          disabled={actionLoading === `imp-${user.uid}`}
                        >
                          Impersonate
                        </button>
                        <button
                          className="admin-btn admin-btn--ghost"
                          onClick={() => navigate(`/admin/users/${user.uid}`)}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="admin-pagination">
              <button
                className="admin-pagination__btn"
                onClick={() => setPage(p => p - 1)}
                disabled={page === 1}
              >
                ← Prev
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                className="admin-pagination__btn"
                onClick={() => setPage(p => p + 1)}
                disabled={page === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </PageLayout>
  );
};

export default AdminUsers;
